import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Users,
} from 'lucide-react';
import { logger } from '@/utils/logger';
import { awsAPI, type DownloadUrlResponse } from '@/services/awsAPI';
import { sessionStorageService } from '@/services/sessionStorageService';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { Header } from '@/components/layout/Header';
import type { ProcessingType } from '@/types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  ResultsSummaryCard,
  DownloadSection,
  AccountCreationPrompt,
} from '@/components/batch/results';



interface AccountInviteStatus {
  invited: boolean;
  requiresConfirmation?: boolean;
  message?: string;
  error?: string;
}

interface ResultsNavigationState {
  jobId: string;
  email?: string;
  customerName?: string;
  companyName?: string;
  processingType?: string;
  results?: {
    successfulRows: number;
    failedRows: number;
    totalProcessed: number;
    downloadUrl?: string;
  };
  actualCost?: number;
  estimatedCost?: number;
  maxAuthorizedCost?: number;
  fileName?: string;
  paymentIntentId?: string;
  accountInviteStatus?: AccountInviteStatus | null;
  elapsedTime?: string | null;
}

interface ResolvedResults {
  jobId: string;
  email?: string;
  customerName?: string;
  companyName?: string;
  processingType?: string;
  successfulRows: number;
  failedRows: number;
  totalProcessed: number;
  downloadUrl?: string;
  actualCost?: number;
  estimatedCost?: number;
  maxAuthorizedCost?: number;
  fileName?: string;
  accountInviteStatus?: AccountInviteStatus | null;
  elapsedTime?: string | null;
}

export const ResultsPage: React.FC = () => {
  const { jobId: jobIdFromParams } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as ResultsNavigationState | undefined;
  const { isAuthenticated } = useCustomerAuth();

  const jobId = navigationState?.jobId ?? jobIdFromParams;

  const [resolved, setResolved] = useState<ResolvedResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [accountCreationError, setAccountCreationError] = useState<string>('');
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    if (!jobId) {
      return;
    }

    const bootstrap = async () => {
      setIsLoading(true);
      setError('');

      try {
        let summary:
          | {
              totalRecords: number;
              successfulRecords: number;
              failedRecords: number;
              downloadUrl?: string;
              expiresAt?: string;
            }
          | null = null;

        try {
          const response = await awsAPI.getJobSummary(jobId);
          summary = {
            totalRecords: response.totalRecords ?? 0,
            successfulRecords: response.successfulRecords ?? 0,
            failedRecords: response.failedRecords ?? 0,
            downloadUrl: response.downloadUrl,
            expiresAt: response.expiresAt,
          };
        } catch (summaryError) {
          logger.warn('Unable to fetch dedicated job summary', summaryError);
          summary = null;
        }

        const navigationResults = navigationState?.results;

        const successfulRows = (navigationResults?.successfulRows ?? summary?.successfulRecords) ?? 0;
        const failedRows = (navigationResults?.failedRows ?? summary?.failedRecords) ?? 0;

        const resolvedResults: ResolvedResults = {
          jobId,
          email: navigationState?.email,
          customerName: navigationState?.customerName,
          companyName: navigationState?.companyName,
          processingType: navigationState?.processingType,
          successfulRows,
          failedRows,
          // Calculate total processed as successful + failed, not from totalRecords
          totalProcessed: successfulRows + failedRows,
          downloadUrl: navigationResults?.downloadUrl ?? summary?.downloadUrl,
          actualCost: navigationState?.actualCost,
          estimatedCost: navigationState?.estimatedCost,
          maxAuthorizedCost: navigationState?.maxAuthorizedCost,
          fileName: navigationState?.fileName,
          accountInviteStatus: navigationState?.accountInviteStatus ?? null,
          elapsedTime: navigationState?.elapsedTime,
        };

        setResolved(resolvedResults);
      } catch (bootstrapError) {
        logger.error('Failed to resolve job results', bootstrapError);
        setError(
          bootstrapError instanceof Error
            ? bootstrapError.message
            : 'Unable to load job results right now. Please try again shortly.',
        );
        setShowErrorModal(true);
      } finally {
        setIsLoading(false);
      }
    };

    bootstrap().catch((fatalError) => {
      logger.error('Unexpected error while loading results', fatalError);
      setIsLoading(false);
      setError('Unexpected error while loading results.');
      setShowErrorModal(true);
    });
  }, [jobId, navigationState]);

  const inferFormatFromResponse = (response: DownloadUrlResponse, requested?: 'csv' | 'json'): string | undefined => {
      const normalizedFormat = response.format?.toString().toLowerCase();
      if (normalizedFormat) {
        return normalizedFormat;
      }

      if (response.contentType) {
        const lowerContentType = response.contentType.toLowerCase();
        if (lowerContentType.includes('csv')) {
          return 'csv';
        }
        if (lowerContentType.includes('json')) {
          return 'json';
        }
      }

      if (response.filename) {
        const filenameExt = response.filename.split('.').pop()?.toLowerCase();
        if (filenameExt) {
          return filenameExt;
        }
      }

      if (response.downloadUrl) {
        const loweredUrl = response.downloadUrl.toLowerCase();

        if (loweredUrl.includes('response-content-type=text%2fcsv')) {
          return 'csv';
        }
        if (loweredUrl.includes('response-content-type=application%2fjson')) {
          return 'json';
        }

        try {
          const url = new URL(response.downloadUrl);
          const urlExt = url.pathname.split('.').pop()?.toLowerCase();
          if (urlExt) {
            return urlExt;
          }
        } catch {
          const match = loweredUrl.match(/\.([a-z0-9]+)(?:\?|$)/);
          if (match && match[1]) {
            return match[1];
          }
        }
      }

      return requested;
    };

    const formatToExtension = (format?: string): string => {
      const normalized = format?.toLowerCase();

      if (normalized === 'csv') {
        return 'csv';
      }

      if (normalized === 'json') {
        return 'json';
      }

      if (normalized === 'txt') {
        return 'txt';
      }

      return 'dat';
    };

    const handleDownload = async () => {
      if (!resolved) {
        return;
      }

      setIsDownloading(true);

      try {
        logger.info(`[INFO] Starting download for job: ${resolved.jobId}`);

        const formatPreferences: Array<'csv' | 'json' | undefined> = ['csv', undefined];
        let downloadResponse: DownloadUrlResponse | null = null;
        let resolvedFormat: string | undefined;
        let obtainedCsv = false;

        for (const preference of formatPreferences) {
          try {
            const response = await awsAPI.getDownloadUrl(
              resolved.jobId,
              preference ? { format: preference } : undefined,
            );

            if (!response.downloadUrl) {
              logger.warn('[WARN] Download URL missing from response payload', response);
              continue;
            }

            const detectedFormat = inferFormatFromResponse(response, preference);
            downloadResponse = response;
            resolvedFormat = detectedFormat;

            if (detectedFormat === 'csv') {
              obtainedCsv = true;
              break;
            }

            if (!preference) {
              break;
            }

            if (response.availableFormats?.includes('csv')) {
              logger.warn(
                `[WARN] CSV format reported as available but response returned ${detectedFormat ?? 'unknown'} for job ${resolved.jobId}`,
              );
            } else {
              logger.warn(
                `[WARN] CSV format not available for job ${resolved.jobId}, received ${detectedFormat ?? 'unknown'}`,
              );
            }
          } catch (formatError) {
            logger.warn(
              `[WARN] Failed to retrieve ${preference ?? 'default'} download URL for job ${resolved.jobId}`,
              formatError,
            );
            if (!preference) {
              throw formatError;
            }
          }
        }

        if (!downloadResponse?.downloadUrl) {
          throw new Error('Download URL missing from response.');
        }

        const filenameSuffix = resolved.jobId.slice(-8);
        const extension = formatToExtension(resolvedFormat);

        if (!obtainedCsv) {
          logger.warn(`[WARN] Falling back to ${resolvedFormat ?? 'unknown'} download for job ${resolved.jobId}`);
          setError('CSV export is not available for this job yet. A JSON file was downloaded instead.');
          setShowErrorModal(true);
        }

        const anchor = document.createElement('a');
        anchor.href = downloadResponse.downloadUrl;
        anchor.download = `collectability-results-${filenameSuffix}.${extension}`;
        anchor.style.display = 'none';

        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        logger.info(
          `[INFO] Download triggered via presigned URL (${obtainedCsv ? 'csv' : resolvedFormat ?? 'fallback'})`,
        );
      } catch (downloadError) {
        logger.error('[ERROR] Failed to download results file', downloadError);
        setError(downloadError instanceof Error ? downloadError.message : 'Failed to download results file.');
        setShowErrorModal(true);
      } finally {
        setIsDownloading(false);
      }
    };
  // const handleEmailResults = async () => {
  //   if (!resolved?.email) {
  //     setError('Email address not available');
  //     setShowErrorModal(true);
  //     return;
  //   }

  //   if (!resolved?.jobId) {
  //     setError('Job ID not available');
  //     setShowErrorModal(true);
  //     return;
  //   }

  //   try {
  //     logger.info('Sending results email', { jobId: resolved.jobId, email: resolved.email });

  //     const result = await awsAPI.emailResults(resolved.jobId, resolved.email);

  //     if (!result.success) {
  //       throw new Error(result.error ?? 'Failed to send email');
  //     }

  //     setEmailSent(true);
  //     logger.info('Results email sent successfully', { jobId: resolved.jobId });
  //   } catch (emailError) {
  //     logger.error('Failed to send results email', emailError);
  //     const errorMessage = emailError instanceof Error ? emailError.message : 'Failed to send email. Please try again.';
  //     setError(errorMessage);
  //     setShowErrorModal(true);
  //   }
  // };

  const handleCreateAccount = async () => {
    if (!resolved || !resolved.email) {
      setAccountCreationError('Email address is required to create an account');
      return;
    }

    setIsCreatingAccount(true);
    setAccountCreationError('');

    try {
      // Extract first and last name from customerName if available
      const nameParts = resolved.customerName?.split(' ') || [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      logger.info('Creating account invite for user:', {
        email: resolved.email,
        firstName,
        lastName,
        companyName: resolved.companyName,
        jobId: resolved.jobId
      });

      const response = await awsAPI.requestAccountInvite({
        email: resolved.email,
        firstName,
        lastName,
        companyName: resolved.companyName || '',
        jobId: resolved.jobId,
        processingType: (resolved.processingType || 'validate') as ProcessingType
      });

      // Check if user already exists (backend returns success=true, userExists=true)
      if (response.success && 'userExists' in response && response.userExists) {
        logger.info('User already exists, showing sign-in prompt');

        // Save batch to session storage
        if (resolved.jobId && resolved.processingType) {
          try {
            sessionStorageService.savePendingBatch({
              jobId: resolved.jobId,
              processingType: resolved.processingType as ProcessingType,
              email: resolved.email,
              customerName: resolved.customerName,
              companyName: resolved.companyName,
              successfulRows: resolved.successfulRows,
              failedRows: resolved.failedRows,
              totalProcessed: resolved.totalProcessed,
              actualCost: resolved.actualCost ?? 0,
              estimatedCost: resolved.estimatedCost ?? 0,
            });
            logger.info('Saved batch job to session storage for post-login import');
          } catch (storageError) {
            logger.warn('Failed to save batch to session storage:', storageError);
          }
        }

        // Show "Already exists" UI
        setAccountCreated(true);
        setAccountCreationError('existing-user');
      } else if (response.success || response.inviteSent) {
        // New account created successfully
        // Save batch job details to session storage for post-login restoration
        if (resolved.jobId && resolved.processingType) {
          try {
            sessionStorageService.savePendingBatch({
              jobId: resolved.jobId,
              processingType: resolved.processingType as ProcessingType,
              email: resolved.email,
              customerName: resolved.customerName,
              companyName: resolved.companyName,
              successfulRows: resolved.successfulRows,
              failedRows: resolved.failedRows,
              totalProcessed: resolved.totalProcessed,
              actualCost: resolved.actualCost ?? 0,
              estimatedCost: resolved.estimatedCost ?? 0,
            });
            logger.info('Saved batch job to session storage for post-login import');
          } catch (storageError) {
            logger.warn('Failed to save batch to session storage:', storageError);
            // Don't fail the whole flow if storage fails
          }
        }

        setAccountCreated(true);
        logger.info('Account invite created successfully:', response);
      } else {
        throw new Error(response.error || 'Failed to create account invite');
      }
    } catch (error) {
      logger.error('Failed to create account:', error);

      // Check if user already exists (from checkout flow)
      const errorMessage = error instanceof Error ? error.message : 'Failed to create account invite';
      const errorCode =
        typeof error === 'object' && error !== null && 'errorCode' in error
          ? (error as { errorCode?: string }).errorCode
          : undefined;

      if (errorCode === 'PASSWORD_NOT_SET' || errorCode === 'PASSWORD_NOT_CONFIRMED') {
        if (resolved.jobId && resolved.processingType) {
          try {
            sessionStorageService.savePendingBatch({
              jobId: resolved.jobId,
              processingType: resolved.processingType as ProcessingType,
              email: resolved.email,
              customerName: resolved.customerName,
              companyName: resolved.companyName,
              successfulRows: resolved.successfulRows,
              failedRows: resolved.failedRows,
              totalProcessed: resolved.totalProcessed,
              actualCost: resolved.actualCost ?? 0,
              estimatedCost: resolved.estimatedCost ?? 0,
            });
            logger.info('Saved batch job to session storage for post-login import (password fallback)');
          } catch (storageError) {
            logger.warn('Failed to save batch to session storage during password fallback:', storageError);
          }
        }

        setAccountCreationError('We created your account, but couldn’t finalize the password automatically. Check your email for the invite to finish setup.');
        setAccountCreated(true);
      } else if (errorMessage.includes('already exists') || errorMessage.includes('UsernameExistsException')) {
        // User already has an account - save batch to session and redirect to login
        logger.info('User already exists, redirecting to login');

        // Save batch job details to session storage
        if (resolved.jobId && resolved.processingType) {
          try {
            sessionStorageService.savePendingBatch({
              jobId: resolved.jobId,
              processingType: resolved.processingType as ProcessingType,
              email: resolved.email,
              customerName: resolved.customerName,
              companyName: resolved.companyName,
              successfulRows: resolved.successfulRows,
              failedRows: resolved.failedRows,
              totalProcessed: resolved.totalProcessed,
              actualCost: resolved.actualCost ?? 0,
              estimatedCost: resolved.estimatedCost ?? 0,
            });
            logger.info('Saved batch job to session storage for post-login import');
          } catch (storageError) {
            logger.warn('Failed to save batch to session storage:', storageError);
          }
        }

        // Set special state to show "Already exists" UI
        setAccountCreated(true);
        setAccountCreationError('existing-user'); // Special flag
      } else {
        // Other error - show generic error message
        setAccountCreationError(errorMessage);
      }
    } finally {
      setIsCreatingAccount(false);
    }
  };

  if (!jobId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="max-w-md mx-4">
          <div className="bg-white rounded-lg  p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No Results Found</h2>
            <p className="text-slate-600 mb-6">
              We could not determine which job to show. Please return to the upload page and start again.
            </p>
            <button
              onClick={() => navigate('/batch')}
              className="ca-btn ca-btn--primary font-semibold transition-colors"
            >
              Start New Processing
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || (!resolved && !error)) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Finalizing your batch results...</p>
        </div>
      </div>
    );
  }

  // If we have an error but no resolved data, show a fallback UI with the modal
  if (!resolved && error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Unable to load results. Please check the error details.</p>
        </div>

        {/* Error Modal */}
        <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Unable to Load Results
              </DialogTitle>
              <DialogDescription className="text-left">
                {error}
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowErrorModal(false)}
                className="ca-btn ca-btn--primary text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }



return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/account')}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <div className="text-xs text-slate-500">Job #{resolved?.jobId.slice(-8) ?? 'Unknown'}</div>
        </div>

        <div className="grid lg:grid-cols-3 gap-3">
          <ResultsSummaryCard
            successfulRows={resolved?.successfulRows ?? 0}
            failedRows={resolved?.failedRows ?? 0}
            totalProcessed={resolved?.totalProcessed ?? 0}
            companyName={resolved?.companyName}
            processingType={resolved?.processingType}
            actualCost={resolved?.actualCost}
            estimatedCost={resolved?.estimatedCost}
            maxAuthorizedCost={resolved?.maxAuthorizedCost}
            fileName={resolved?.fileName}
            elapsedTime={resolved?.elapsedTime}
          />

          <div className="space-y-4">
            <DownloadSection
              isDownloading={isDownloading}
              onDownload={handleDownload}
            />

            {!isAuthenticated && (
              <AccountCreationPrompt
                accountInviteStatus={resolved?.accountInviteStatus}
                onCreateAccount={handleCreateAccount}
                email={resolved?.email}
                isCreatingAccount={isCreatingAccount}
                accountCreated={accountCreated}
                accountCreationError={accountCreationError}
              />
            )}

            <div className="ca-card p-4 text-center">
              <BarChart3 className="w-6 h-6 text-slate-600 mx-auto mb-2" />
              <h3 className="text-base font-semibold text-slate-900 mb-1">Process another file</h3>
              <p className="text-xs text-slate-600 mb-3">Need more batches processed? Upload the next file.</p>
              <button
                onClick={() => navigate('/batch')}
                className="w-full inline-flex items-center justify-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 py-2 rounded-md text-sm font-medium"
              >
                Upload new file
              </button>
            </div>

            <div className="ca-card p-4 text-center">
              <Users className="w-6 h-6 text-slate-600 mx-auto mb-2" />
              <h3 className="text-base font-semibold text-slate-900 mb-1">Retention policy</h3>
              <p className="text-xs text-slate-600">
                Batch results are stored for 90 days and can be re-downloaded anytime from your account history. Download links expire after 7 days but new links can be generated on demand.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Error Modal */}
      <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Unable to Load Results
            </DialogTitle>
            <DialogDescription className="text-left">
              {error}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setShowErrorModal(false)}
              className="ca-btn ca-btn--primary text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResultsPage;







